import { Button, Input, Stack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { WorkOrderInput } from "../type/WorkOrder";
import { workOrderSchema } from "../schemas/workOrderSchema";
import { useEffect } from "react";
import { useAuth } from "@/shared/session";
import { useGetClientEntities } from "@/core/clientEntities/useCases/useGetClientEntities";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { NativeSelectField, NativeSelectRoot } from "@/ui/chakra/native-select";
import { SimilarRecordsHint } from "@/ui/SimilarRecordsHint";
import { useSimilarWorkOrders } from "../useCases/useSimilarWorkOrders";

type WorkOrderFormInput = z.infer<typeof workOrderSchema>;

const EMPTY_FORM: WorkOrderFormInput = {
  name: "",
  code: "",
  clientEntityId: "",
  zoneId: "",
  enabled: true,
};

interface CreateWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WorkOrderInput) => void;
}

export const CreateWorkOrderModal = ({
  isOpen,
  onClose,
  onSubmit,
}: CreateWorkOrderModalProps) => {
  const { user, session } = useAuth();

  const { zones } = user || {};

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<WorkOrderFormInput>({
    resolver: zodResolver(workOrderSchema),
    defaultValues: { ...EMPTY_FORM, zoneId: session?.zone ?? "" },
  });

  const zoneId = watch("zoneId");

  // Client entities belong to zones, so only offer the ones of the chosen zone.
  const { data: clientEntities, isPending: isClientEntitiesLoading } =
    useGetClientEntities({ zoneIds: zoneId || undefined });

  const similarWorkOrders = useSimilarWorkOrders({
    name: watch("name"),
    code: watch("code"),
    zoneId,
  });

  useEffect(() => {
    if (isOpen) reset({ ...EMPTY_FORM, zoneId: session?.zone ?? "" });
  }, [isOpen, reset, session?.zone]);

  useEffect(() => {
    const clientEntityId = getValues("clientEntityId");
    if (
      clientEntityId &&
      !clientEntities.some((clientEntity) => clientEntity.id === clientEntityId)
    )
      setValue("clientEntityId", "");
  }, [clientEntities, getValues, setValue]);

  const onFormSubmit = (data: WorkOrderInput) => {
    onSubmit(data);
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Crear orden de trabajo</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              <Field
                label="Nombre"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register("name")}
                  placeholder="Nombre"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Código"
                required
                invalid={!!errors.code}
                errorText={errors.code?.message}
              >
                <Input
                  {...register("code")}
                  placeholder="Código"
                  autoComplete="off"
                />
              </Field>
              <SimilarRecordsHint
                items={similarWorkOrders}
                entityLabel="órdenes de trabajo"
                mt={-2}
              />
              <Field
                label="Zona"
                required
                invalid={!!errors.zoneId}
                errorText={errors.zoneId?.message}
              >
                <NativeSelectRoot disabled={zones?.length === 0}>
                  <NativeSelectField
                    {...register("zoneId")}
                    placeholder="Seleccione una zona"
                  >
                    {zones?.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
              <Field
                label="Empresa"
                required
                invalid={!!errors.clientEntityId}
                errorText={errors.clientEntityId?.message}
              >
                <NativeSelectRoot disabled={!zoneId || isClientEntitiesLoading}>
                  <NativeSelectField
                    {...register("clientEntityId")}
                    placeholder={
                      zoneId
                        ? "Seleccione una empresa"
                        : "Seleccione primero una zona"
                    }
                  >
                    {clientEntities?.map((clientEntity) => (
                      <option key={clientEntity.id} value={clientEntity.id}>
                        {clientEntity.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button
              colorPalette="brand"
              type="submit"
              loading={zones?.length === 0}
            >
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
